import {
  AlertCircle,
  Building,
  Check,
  CreditCard,
  Download,
  Edit,
  Eye,
  FileText,
  Grid3X3,
  List as ListIcon,
  MoreHorizontal,
  Plus,
  Search,
  User,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DatePicker } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { ManpowerEntryForm } from '../components/manpower';
import ManpowerLogManagement from '../components/manpower/ManpowerLogManagement';
import { useToast } from '../contexts/ToastContext';
import manpowerService from '../services/manpowerService';
import {
  ManpowerEntry,
  ManpowerFormData,
  ManpowerPermissions,
  ManpowerQuery,
  ManpowerStats,
  Project,
} from '../types';

const Manpower: React.FC = () => {
  const _navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  // State Management
  const [entries, setEntries] = useState<ManpowerEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<ManpowerPermissions>({
    view: false,
    log: false,
    approve: false,
    pay: false,
  });
  const [stats, setStats] = useState<ManpowerStats | null>(null);

  // Project Selection
  const [selectedProject, setSelectedProject] = useState('');
  const [projectRequired, _setProjectRequired] = useState(true);

  // View and Selection
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [_currentPage, setCurrentPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);

  // Modals and Actions
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [_showBulkActions, _setShowBulkActions] = useState(false);
  const [_bulkActionType, _setBulkActionType] = useState<'approve' | 'pay' | 'delete' | null>(null);
  const [submittingEntry, setSubmittingEntry] = useState(false);
  const [useNewLogSystem, setUseNewLogSystem] = useState(true); // Toggle between old and new systems

  // Filters and Search
  const [query, setQuery] = useState<ManpowerQuery>({
    page: 1,
    limit: 20,
    sort_by: 'logged_at',
    sort_order: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Dynamic dropdown state
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);

  const sortOptions = [
    { value: 'logged_at', label: 'Date Logged' },
    { value: 'date', label: 'Work Date' },
    { value: 'hours_worked', label: 'Hours Worked' },
    { value: 'total_payment', label: 'Total Payment' },
    { value: 'status', label: 'Status' },
  ];

  // Load initial data
  useEffect(() => {
    loadPermissions();
    loadProjects();
    loadDropdownOptions();
  }, []);

  // Load data when permissions are available
  useEffect(() => {
    if (permissions.view && selectedProject) {
      loadData();
      loadStats();
    }
  }, [permissions.view, selectedProject, query]);

  // Auto-select first project if only one available
  useEffect(() => {
    if (projects.length === 1 && !selectedProject) {
      setSelectedProject(projects[0].id);
    }
  }, [projects]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await manpowerService.getPermissions();
      setPermissions(userPermissions);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const projectsData = await manpowerService.getProjectsForDropdown();
      setProjects(
        projectsData.map((p: any) => ({
          id: p.value,
          name: p.label,
          status: p.metadata?.status || 'active',
          priority: p.metadata?.priority || 'medium',
        }))
      );
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadDropdownOptions = async () => {
    try {
      const [statusData, roleData] = await Promise.all([
        manpowerService.getStatusOptions(),
        manpowerService.getWorkerTypes(),
      ]);

      setStatusOptions([{ value: '', label: 'All Status' }, ...statusData]);

      setRoleOptions([{ value: '', label: 'All Roles' }, ...roleData]);
    } catch (error) {
      console.error('Failed to load dropdown options:', error);
    }
  };

  const loadData = async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const result = await manpowerService.getManpowerEntries(selectedProject, query);
      if (result.success && result.data) {
        setEntries(result.data.entries || []);
        setTotalEntries(result.data.total || 0);
        setTotalPages(result.data.totalPages || 1);
      } else {
        setEntries([]);
        setTotalEntries(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to load manpower entries:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!selectedProject) return;

    try {
      const result = await manpowerService.getManpowerStats(selectedProject);
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Update query when filters change
  useEffect(() => {
    const newQuery: ManpowerQuery = {
      ...query,
      project_id: selectedProject || undefined,
      status: selectedStatus || undefined,
      role: selectedRole || undefined,
      search: searchTerm || undefined,
      date_from: dateRange.from || undefined,
      date_to: dateRange.to || undefined,
      page: 1,
    };
    setQuery(newQuery);
    setCurrentPage(1);
  }, [selectedProject, selectedStatus, selectedRole, searchTerm, dateRange]);

  // Selection handlers
  const toggleEntrySelection = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const selectAllEntries = () => {
    setSelectedEntries(new Set(entries.map(e => e.entry_id)));
  };

  const clearSelection = () => {
    setSelectedEntries(new Set());
  };

  // Bulk operations
  const handleBulkAction = async (action: 'approve' | 'pay' | 'delete', notes?: string) => {
    if (selectedEntries.size === 0) return;

    try {
      await manpowerService.bulkAction({
        entry_ids: Array.from(selectedEntries),
        action,
        notes,
      });

      clearSelection();
      loadData();
      loadStats();
    } catch (error) {
      console.error(`Bulk ${action} failed:`, error);
    }
  };

  // Individual entry actions
  const handleApprove = async (entryId: string) => {
    try {
      await manpowerService.approveEntry(entryId);
      loadData();
      loadStats();
    } catch (error) {
      console.error('Approve failed:', error);
    }
  };

  const handleMarkAsPaid = async (entryId: string, notes?: string) => {
    try {
      await manpowerService.markAsPaid(entryId, notes);
      loadData();
      loadStats();
    } catch (error) {
      console.error('Mark as paid failed:', error);
    }
  };

  // Handle manpower entry form submission
  const handleEntrySubmit = async (formData: ManpowerFormData) => {
    setSubmittingEntry(true);
    try {
      console.log('Submitting manpower entries:', formData);

      // Create multiple entries - one for each entry row
      const submissions = formData.entries.map(async entry => {
        // Transform the entry row data back to the expected API format
        const entryData = {
          project_id: formData.project_id,
          entry_type: entry.entry_type,
          worker_id: entry.worker_id,
          worker_name: entry.worker_name,
          company_id: entry.company_id,
          company_name: entry.company_name,
          number_of_workers: entry.number_of_workers,
          role: entry.role,
          location: formData.location,
          date: formData.date,
          hours_worked: entry.hours_worked,
          overtime_hours: entry.overtime_hours,
          hourly_rate: entry.hourly_rate,
          overtime_rate: entry.overtime_rate,
          comments: entry.comments,
          attachments: formData.attachments,
        };

        return manpowerService.createEntry(entryData);
      });

      // Wait for all submissions to complete
      const results = await Promise.allSettled(submissions);

      // Check results
      const successful = results.filter(
        result => result.status === 'fulfilled' && result.value && result.value.success !== false
      ).length;

      const failed = results.length - successful;

      if (successful > 0) {
        await loadData(); // Refresh the data
        await loadStats(); // Refresh stats
        setShowEntryModal(false);

        if (failed === 0) {
          toast.success(t('manpower.toast.allEntriesSuccess', { count: successful }));
        } else {
          toast.warning(t('manpower.toast.partialSuccess', { successful, failed }));
        }
      } else {
        console.error('All entries failed to submit:', results);
        toast.error(t('manpower.toast.submitEntriesFailedCheck'));
      }
    } catch (error) {
      console.error('Error submitting entries:', error);
      toast.error(t('manpower.toast.submitEntriesFailed'));
    } finally {
      setSubmittingEntry(false);
    }
  };

  // Utility functions
  const formatCurrency = (amount: number) => manpowerService.formatCurrency(amount);
  const formatHours = (hours: number) => manpowerService.formatHours(hours);
  const getStatusColor = (status: string) => manpowerService.getStatusColor(status);

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString));
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  // Permission check - hide module if no view permission
  if (!permissions.view) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <div className="text-center py-12">
          <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-xs text-gray-500">
            You don't have permission to view the Manpower module.
          </p>
        </div>
      </div>
    );
  }

  // Project selection required
  if (projectRequired && !selectedProject && projects.length > 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <div className="text-center py-12">
          <Building className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">Select a Project</h3>
          <p className="text-xs text-gray-500 mb-6">
            Please select a project to view manpower entries.
          </p>
          <div className="max-w-md mx-auto">
            <Dropdown
              label=""
              placeholder="Select a project"
              options={projects.map(p => ({ value: p.id, label: p.name }))}
              value={selectedProject}
              onChange={setSelectedProject}
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading && !entries.length) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <>
      <div className="absolute inset-0 flex flex-col">
        {/* Compact Header Bar */}
        <div className="flex-none bg-white border-b border-gray-200">
          <div className="px-5 py-2.5">
            <div className="flex items-center justify-between">
              {/* Left: Title + Inline Stats */}
              <div className="flex items-center space-x-5">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                    <Users className="h-4 w-4 text-brand-600" />
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-gray-900 leading-tight">
                      Manpower
                    </h1>
                    <p className="text-[11px] text-gray-500 leading-tight">
                      Track labor hours and manage payroll
                    </p>
                  </div>
                </div>

                {/* Inline Stats - Compact */}
                {stats && selectedProject && (
                  <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-gray-900">
                        {stats.totalWorkers || 0}
                      </span>
                      <span className="text-[10px] text-gray-400">Workers</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-green-600">
                        {formatHours(stats.totalHours || 0)}
                      </span>
                      <span className="text-[10px] text-gray-400">Hours</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-brand-600">
                        {formatCurrency(stats.totalCost || 0)}
                      </span>
                      <span className="text-[10px] text-gray-400">Cost</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex items-center space-x-2">
                {/* Project Selector - Compact */}
                {projects.length > 1 && (
                  <div className="w-48">
                    <Dropdown
                      label=""
                      placeholder="Select project"
                      options={projects.map(p => ({ value: p.id, label: p.name }))}
                      value={selectedProject}
                      onChange={setSelectedProject}
                    />
                  </div>
                )}

                {/* System Toggle - Compact */}
                <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                  <button
                    onClick={() => setUseNewLogSystem(false)}
                    className={`px-2 py-1 text-[11px] rounded transition-colors ${
                      !useNewLogSystem
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Legacy
                  </button>
                  <button
                    onClick={() => setUseNewLogSystem(true)}
                    className={`px-2 py-1 text-[11px] rounded transition-colors ${
                      useNewLogSystem
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Enhanced
                  </button>
                </div>

                {!useNewLogSystem && permissions.log && selectedProject && (
                  <button
                    onClick={() => setShowEntryModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md transition-colors"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Log Hours
                  </button>
                )}

                <button
                  onClick={() => setShowPayrollModal(true)}
                  className="inline-flex items-center px-3 py-1.5 text-[11px] font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  Payroll
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 p-4 bg-gray-50 overflow-auto">
          {/* Enhanced Log System */}
          {useNewLogSystem ? (
            <ManpowerLogManagement
              projectId={selectedProject}
              projects={projects}
              showProjectSelector={projects.length > 1}
            />
          ) : (
            <>
              {/* Legacy System - Stats Dashboard */}
              {stats && selectedProject && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-red-50 text-red-600 border-red-200 rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Total Cost</p>
                        <p className="text-2xl font-bold mt-1">
                          {formatCurrency(stats.totalCost || 0)}
                        </p>
                        <p className="text-xs mt-1 opacity-60">↗ +15% this week</p>
                      </div>
                      <div className="text-2xl opacity-75">💰</div>
                    </div>
                  </div>

                  <div className="bg-green-50 text-green-600 border-green-200 rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Total Hours</p>
                        <p className="text-2xl font-bold mt-1">
                          {formatHours(stats.totalHours || 0)}
                        </p>
                        <p className="text-xs mt-1 opacity-60">↗ +8% this week</p>
                      </div>
                      <div className="text-2xl opacity-75">⏰</div>
                    </div>
                  </div>

                  <div className="bg-purple-50 text-purple-600 border-purple-200 rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Active Workers</p>
                        <p className="text-2xl font-bold mt-1">{stats.totalWorkers || 0}</p>
                        <p className="text-xs mt-1 opacity-60">↗ +3 this week</p>
                      </div>
                      <div className="text-2xl opacity-75">👷</div>
                    </div>
                  </div>

                  <div className="bg-brand-50 text-brand-primary border-brand-200 rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-75">Total Entries</p>
                        <p className="text-2xl font-bold mt-1">
                          {stats.totalEntries || entries.length}
                        </p>
                        <p className="text-xs mt-1 opacity-60">↗ +12% this week</p>
                      </div>
                      <div className="text-2xl opacity-75">📋</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Filters and Search */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search entries..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  <Dropdown
                    label=""
                    placeholder="All Status"
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                  />

                  <Dropdown
                    label=""
                    placeholder="All Roles"
                    options={roleOptions}
                    value={selectedRole}
                    onChange={setSelectedRole}
                  />

                  <DatePicker
                    placeholder="From Date"
                    value={dateRange.from}
                    onChange={date => setDateRange(prev => ({ ...prev, from: date || '' }))}
                  />

                  <DatePicker
                    placeholder="To Date"
                    value={dateRange.to}
                    onChange={date => setDateRange(prev => ({ ...prev, to: date || '' }))}
                  />

                  <Dropdown
                    label=""
                    placeholder="Sort by Date"
                    options={sortOptions}
                    value={query.sort_by || 'logged_at'}
                    onChange={value => setQuery(prev => ({ ...prev, sort_by: value as any }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {totalEntries} entr{totalEntries !== 1 ? 'ies' : 'y'} found
                  </span>

                  {/* View Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'list'
                          ? 'bg-white text-red-500 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title="List View"
                    >
                      <ListIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-white text-red-500 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title="Grid View"
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedEntries.size > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-red-800 font-medium">
                      {selectedEntries.size} entr{selectedEntries.size !== 1 ? 'ies' : 'y'} selected
                    </span>
                    <div className="flex space-x-2">
                      {permissions.approve && (
                        <button
                          onClick={() => handleBulkAction('approve')}
                          className="px-3 py-1 text-sm text-brand-700 hover:bg-brand-100 rounded-md transition-colors"
                        >
                          Approve All
                        </button>
                      )}
                      {permissions.pay && (
                        <button
                          onClick={() => handleBulkAction('pay')}
                          className="px-3 py-1 text-sm text-green-700 hover:bg-green-100 rounded-md transition-colors"
                        >
                          Mark as Paid
                        </button>
                      )}
                      <button
                        onClick={clearSelection}
                        className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Entries Display */}
              <div className="space-y-6">
                {/* List View */}
                {viewMode === 'list' && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <input
                                type="checkbox"
                                checked={
                                  selectedEntries.size === entries.length && entries.length > 0
                                }
                                onChange={e => {
                                  if (e.target.checked) {
                                    selectAllEntries();
                                  } else {
                                    clearSelection();
                                  }
                                }}
                                className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                              />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Worker/Company & Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Hours & Payment
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="relative px-6 py-3">
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {entries.map(entry => (
                            <tr key={entry.entry_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedEntries.has(entry.entry_id)}
                                  onChange={() => toggleEntrySelection(entry.entry_id)}
                                  className="rounded border-gray-300 text-red-500 focus:ring-red-500"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    <div
                                      className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                        entry.worker_id ? 'bg-brand-100' : 'bg-purple-100'
                                      }`}
                                    >
                                      {entry.worker_id ? (
                                        <User
                                          className={`h-5 w-5 ${entry.worker_id ? 'text-brand-primary' : 'text-purple-600'}`}
                                        />
                                      ) : (
                                        <Building className="h-5 w-5 text-purple-600" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {entry.worker_name || entry.company_name}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {entry.numberOfWorkers > 1
                                        ? `${entry.numberOfWorkers} workers`
                                        : '1 worker'}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {entry.roleDescription}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {formatHours(entry.totalHours)}
                                  {entry.overtimeHours > 0 && (
                                    <span className="text-red-500">
                                      {' '}
                                      +{formatHours(entry.overtimeHours)} OT
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {formatCurrency(entry.costs?.totalCost || 0)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.status)}`}
                                >
                                  {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(entry.date?.toString() || new Date().toISOString())}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button
                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  {permissions.log && (
                                    <button
                                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                      title="Edit"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                  )}
                                  {permissions.approve && entry.status === 'pending' && (
                                    <button
                                      onClick={() => handleApprove(entry.id)}
                                      className="p-1 text-brand-primary hover:text-brand-800 hover:bg-brand-100 rounded transition-colors"
                                      title="Approve"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                  )}
                                  {permissions.pay && entry.status === 'confirmed' && (
                                    <button
                                      onClick={() => handleMarkAsPaid(entry.id)}
                                      className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                                      title="Mark as Paid"
                                    >
                                      <CreditCard className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    title="More actions"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {entries.map(entry => (
                      <div
                        key={entry.id}
                        className={`bg-white rounded-xl shadow-sm border transition-all hover:shadow-md ${
                          selectedEntries.has(entry.id)
                            ? 'ring-2 ring-red-500 border-red-200'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedEntries.has(entry.id)}
                                onChange={() => toggleEntrySelection(entry.id)}
                                className="rounded border-gray-300 text-red-500 focus:ring-red-500 mr-3"
                              />
                              <div
                                className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                                  entry.worker_id ? 'bg-brand-100' : 'bg-purple-100'
                                }`}
                              >
                                {entry.worker_id ? (
                                  <User className="h-5 w-5 text-brand-primary" />
                                ) : (
                                  <Building className="h-5 w-5 text-purple-600" />
                                )}
                              </div>
                            </div>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.status)}`}
                            >
                              {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                            </span>
                          </div>

                          <div className="mb-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-1">
                              {entry.worker_name || entry.company_name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {entry.workers && entry.workers.length > 0
                                ? entry.workers[0].role
                                : 'Worker'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {getProjectName(entry.projectId)}
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                            <div>
                              <span className="text-gray-500">Hours</span>
                              <p className="font-medium">
                                {formatHours(entry.totalHours)}
                                {entry.overtimeHours > 0 && (
                                  <span className="text-red-500 text-xs">
                                    {' '}
                                    +{formatHours(entry.overtimeHours)} OT
                                  </span>
                                )}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Payment</span>
                              <p className="font-medium text-gray-900">
                                {formatCurrency(entry.costs?.totalCost || 0)}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Workers</span>
                              <p className="font-medium">{entry.workers?.length || 0}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date</span>
                              <p className="font-medium">
                                {formatDate(entry.date?.toString() || new Date().toISOString())}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            <div className="flex items-center space-x-2">
                              <button
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {permissions.log && (
                                <button
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                            </div>

                            <div className="flex items-center space-x-2">
                              {permissions.approve && entry.status === 'pending' && (
                                <button
                                  onClick={() => handleApprove(entry.id)}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-brand-700 bg-brand-100 rounded-full hover:bg-brand-200 transition-colors"
                                >
                                  <Check className="h-3 w-3 mr-1" />
                                  Approve
                                </button>
                              )}
                              {permissions.pay && entry.status === 'confirmed' && (
                                <button
                                  onClick={() => handleMarkAsPaid(entry.id)}
                                  className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  Pay
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {entries.length === 0 && selectedProject && (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No manpower entries found
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {searchTerm || selectedStatus || selectedRole
                        ? 'Try adjusting your search or filter criteria.'
                        : 'Get started by logging your first manpower entry.'}
                    </p>
                    {permissions.log && (
                      <button
                        onClick={() => setShowEntryModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Log Hours
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Entry Modal */}
      {showEntryModal &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
              onClick={() => setShowEntryModal(false)}
            />

            <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right duration-300">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <Plus className="h-5 w-5 text-red-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Add Manpower Entry</h2>
                  </div>
                  <button
                    onClick={() => setShowEntryModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <ManpowerEntryForm
                    onSubmit={handleEntrySubmit}
                    onCancel={() => setShowEntryModal(false)}
                    projects={projects}
                    isSubmitting={submittingEntry}
                    projectId={selectedProject}
                    initialData={{ project_id: selectedProject }}
                  />
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Payroll Modal Placeholder */}
      {showPayrollModal &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
              onClick={() => setShowPayrollModal(false)}
            />

            <div className="fixed top-6 right-6 bottom-6 z-[100] w-[720px] animate-in slide-in-from-right duration-300">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-100 p-2 rounded-lg">
                      <FileText className="h-5 w-5 text-brand-primary" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900">Monthly Payroll Summary</h2>
                  </div>
                  <button
                    onClick={() => setShowPayrollModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Payroll Summary</h3>
                      <p className="text-gray-600">
                        Payroll summary component will be implemented next
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex-shrink-0">
                  <button
                    onClick={() => setShowPayrollModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default Manpower;
